import React, { useEffect, useRef } from "react";

const palettes = {
  cream: ["#141410", "#2246E8", "#F04438", "#FFD43B"],
  cobalt: ["#FFFDF8", "#FFD43B", "#F04438", "#8FA2FF"],
  red: ["#141410", "#FFFDF8", "#FFD43B", "#2246E8"],
  signal: ["#A9FF1C", "#FF4D1F", "#EDF0E6", "#2A302A"],
};

function drawThreads(p, colours, time) {
  p.noFill();
  for (let line = 0; line < 14; line += 1) {
    const x = (line / 13) * p.width;
    p.stroke(colours[line % colours.length]);
    p.strokeWeight(line % 4 === 0 ? 2.2 : 0.9);
    p.beginShape();
    for (let y = -24; y <= p.height + 24; y += 18) {
      const drift =
        p.noise(line * 0.18, y * 0.0045, time * 0.12) * 86 - 43;
      p.vertex(x + drift, y);
    }
    p.endShape();
  }
}

function drawOrbit(p, colours, time) {
  p.noFill();
  p.translate(p.width * 0.56, p.height * 0.5);
  const limit = Math.min(p.width, p.height);
  for (let ring = 0; ring < 12; ring += 1) {
    p.push();
    p.rotate(time * (ring % 2 === 0 ? 0.03 : -0.025) + ring * 0.18);
    p.stroke(colours[ring % colours.length]);
    p.strokeWeight(ring % 3 === 0 ? 2 : 0.8);
    const diameter = limit * (0.18 + ring * 0.09);
    p.ellipse(0, 0, diameter, diameter * (0.38 + (ring % 4) * 0.08));
    p.pop();
  }
}

function drawField(p, colours, time) {
  p.noStroke();
  const columns = Math.max(9, Math.floor(p.width / 70));
  const rows = Math.max(6, Math.floor(p.height / 70));
  for (let y = 0; y <= rows; y += 1) {
    for (let x = 0; x <= columns; x += 1) {
      const px = (x / columns) * p.width;
      const py = (y / rows) * p.height;
      const noise = p.noise(x * 0.16, y * 0.16, time * 0.08);
      const size = 2 + noise * 8;
      p.fill(colours[(x + y) % colours.length]);
      p.circle(px + (noise - 0.5) * 28, py + (noise - 0.5) * 28, size);
    }
  }
}

function drawScan(p, colours, time) {
  p.noFill();
  const rows = Math.max(7, Math.floor(p.height / 64));
  for (let row = 0; row <= rows; row += 1) {
    p.stroke(colours[row % colours.length]);
    p.strokeWeight(row % 4 === 0 ? 1.6 : 0.7);
    p.beginShape();
    for (let x = -20; x <= p.width + 20; x += 18) {
      const signal =
        Math.sin(x * 0.018 + time * 0.75 + row * 0.8) * 7 +
        (p.noise(x * 0.008, row * 0.2, time * 0.05) - 0.5) * 34;
      p.vertex(x, (row / rows) * p.height + signal);
    }
    p.endShape();
  }

  const cursor = ((time * 42) % (p.width + 60)) - 30;
  p.stroke(colours[1]);
  p.strokeWeight(1.4);
  p.line(cursor, 0, cursor, p.height);
}

export default function P5Backdrop({
  variant = "threads",
  palette = "cream",
  className = "",
}) {
  const hostRef = useRef(null);

  useEffect(() => {
    let instance;
    let resizeObserver;
    let cancelled = false;

    async function mountSketch() {
      const p5Module = await import("p5");
      if (cancelled || !hostRef.current) return;
      const P5 = p5Module.default || p5Module;
      const host = hostRef.current;
      const colours = palettes[palette] || palettes.cream;
      const reduceMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)"
      ).matches;

      instance = new P5((p) => {
        const sizeCanvas = () => {
          const width = Math.max(1, host.clientWidth);
          const height = Math.max(1, host.clientHeight);
          if (p.width && p.height) {
            p.resizeCanvas(width, height, true);
          }
          return { width, height };
        };

        p.setup = () => {
          const { width, height } = sizeCanvas();
          const canvas = p.createCanvas(width, height);
          canvas.elt.setAttribute("aria-hidden", "true");
          p.pixelDensity(1);
          p.frameRate(reduceMotion ? 1 : 24);
          resizeObserver = new ResizeObserver(sizeCanvas);
          resizeObserver.observe(host);
        };

        p.draw = () => {
          p.clear();
          const time = reduceMotion ? 4 : p.frameCount / 24;
          if (variant === "orbit") drawOrbit(p, colours, time);
          else if (variant === "scan") drawScan(p, colours, time);
          else if (variant === "field") drawField(p, colours, time);
          else drawThreads(p, colours, time);
          if (reduceMotion) p.noLoop();
        };
      }, host);
    }

    mountSketch();

    return () => {
      cancelled = true;
      resizeObserver?.disconnect();
      instance?.remove();
    };
  }, [palette, variant]);

  return (
    <div
      ref={hostRef}
      className={`p5-backdrop ${className}`}
      aria-hidden="true"
    />
  );
}
